/**
 * HighRiskRecords - Top 5 high-risk records panel
 *
 * Uses the shared RecordsTable component for consistent record display.
 * Styled to match the Eligible Complaints card on the Today page.
 */

import { Card, CardContent, CardHeader } from '../../../../../ui/card';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { RecordsTable } from '../molecules';
import type { HighRiskRecordsProps } from '../../types';
import { HIGH_RISK_THRESHOLD } from '../../constants';

export function HighRiskRecords({
  data,
  onRecordClick,
  onCauseClick,
  isLoading,
  isError,
  className,
}: HighRiskRecordsProps) {
  const isEmpty = !data || data.records.length === 0;

  if (isError) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} data-testid="high-risk-records">
        <CardHeader className="border-b border-border/70 px-4 pb-3 pt-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <h3 className="text-base font-semibold text-foreground">High-Risk Records</h3>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="h-48 flex items-center justify-center text-destructive">
            Failed to load high-risk records
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}
      data-testid="high-risk-records"
      aria-busy={isLoading}
    >
      {/* Header - matching ActionCard style */}
      <CardHeader className="border-b border-border/70 px-4 pb-3 pt-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <h3 className="text-base font-semibold text-foreground">High-Risk Records</h3>
        </div>
        {!isLoading && !isEmpty && (
          <div className="flex flex-col items-end -mt-1">
            <span className="font-jakarta text-2xl font-extrabold tracking-[-0.04em] text-foreground">{data.totalHighRisk}</span>
            <span className="text-xs text-muted-foreground">above threshold</span>
          </div>
        )}
      </CardHeader>

      {/* Table */}
      <CardContent className="p-0">
        <RecordsTable
          records={data?.records ?? []}
          onRecordClick={onRecordClick}
          onCauseClick={onCauseClick}
          isLoading={isLoading}
          emptyMessage={`No high-risk records found (score > ${HIGH_RISK_THRESHOLD})`}
          loadingRows={3}
          actionIcon="external"
        />
      </CardContent>
    </Card>
  );
}
